import { useState, useEffect } from 'react';
import axios from 'axios';
import './ProfileScreen.css';
import { useUser } from '../../context/UserContext';
import { getAuthToken } from '../../utils/authStorage';

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

export default function ProfileScreen() {
  const { loadUser } = useUser();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // edit profile
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState(null); // { type: 'ok'|'err', text }
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  // change password
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getAuthToken()}` },
  });

  useEffect(() => {
    axios
      .get('/api/users/me', authHeaders())
      .then((res) => {
        const normalized = {
          ...res.data,
          fullName: res.data.fullName || res.data.clientName || '',
        };
        setUser(normalized);
        setEditName(normalized.fullName);
        setEditEmail(res.data.email || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  /* ── Toggle notifications ── */
  const handleNotifToggle = async () => {
    const next = !user.emailNotificationsEnabled;
    try {
      const res = await axios.put(
        '/api/users/me',
        { emailNotificationsEnabled: next },
        authHeaders()
      );
      setUser(res.data);
    } catch {
      setProfileMsg({ type: 'err', text: 'Could not update the preference.' });
    }
  };

  /* ── Guardar perfil ── */
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      const res = await axios.put(
        '/api/users/me',
        { fullName: editName, email: editEmail },
        authHeaders()
      );
      setUser({
        ...res.data,
        fullName: res.data.fullName || res.data.clientName || '',
      });
      await loadUser();
      setProfileMsg({ type: 'ok', text: 'Profile updated.' });
    } catch (err) {
      const msg = err.response?.data?.message || 'Could not save changes.';
      setProfileMsg({ type: 'err', text: msg });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleTestEmail = async () => {
    setSendingTestEmail(true);
    setProfileMsg(null);
    try {
      await axios.post('/api/users/me/test-email', {}, authHeaders());
      setProfileMsg({
        type: 'ok',
        text: `Test email sent to ${user.email}.`,
      });
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Could not send the test email.';
      setProfileMsg({ type: 'err', text: message });
    } finally {
      setSendingTestEmail(false);
    }
  };

  /* ── Change password ── */
  const handleChangePw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      setPwMsg({ type: 'err', text: 'New passwords do not match.' });
      return;
    }
    if (newPw.length < 8) {
      setPwMsg({ type: 'err', text: 'Password must be at least 8 characters long.' });
      return;
    }
    setSavingPw(true);
    setPwMsg(null);
    try {
      await axios.put(
        '/api/users/me/password',
        { currentPassword: currentPw, newPassword: newPw },
        authHeaders()
      );
      setPwMsg({ type: 'ok', text: 'Password updated successfully.' });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      const msg = err.response?.data?.message || 'Current password is incorrect.';
      setPwMsg({ type: 'err', text: msg });
    } finally {
      setSavingPw(false);
    }
  };

  if (loading) return <div className="prof-loading">Loading profile…</div>;
  if (!user) return <div className="prof-loading">Could not load the profile.</div>;

  return (
    <div className="prof-page">
      <div className="prof-head">
        <h1 className="prof-title">My profile</h1>
        <p className="prof-subtitle">Manage your personal information and preferences</p>
      </div>

      <div className="prof-grid">

        {/* ── Avatar + info ── */}
        <div className="prof-card prof-identity">
          <div className="prof-avatar">{getInitials(user.fullName || user.clientName)}</div>
          <div>
            <p className="prof-name">{user.fullName || user.clientName || '—'}</p>
            <p className="prof-email">{user.email}</p>
            <span className="prof-badge">{user.tipo === 'ORGANIZACION' ? 'Organization' : 'Individual'}</span>
          </div>
        </div>

        {/* ── Editar datos ── */}
        <div className="prof-card">
          <h2 className="prof-card-title">Personal information</h2>
          <form onSubmit={handleSaveProfile} className="prof-form">
            <label className="prof-label">
              Full name
              <input
                className="prof-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Your name"
              />
            </label>
            <label className="prof-label">
              Email
              <input
                className="prof-input"
                type="email"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>
            {profileMsg && (
              <p className={`prof-msg ${profileMsg.type === 'ok' ? 'prof-msg--ok' : 'prof-msg--err'}`}>
                {profileMsg.text}
              </p>
            )}
            <button className="prof-btn prof-btn--primary" type="submit" disabled={savingProfile}>
              {savingProfile ? 'Saving…' : 'Save changes'}
            </button>
          </form>
        </div>

        {/* ── Notifications ── */}
        <div className="prof-card">
          <h2 className="prof-card-title">Notifications</h2>
          <p className="prof-card-desc">
            Receive an email when image analysis for an inspection finishes.
          </p>
          <div className="prof-notif-row">
            <div>
              <p className="prof-notif-label">Email notifications</p>
              <p className="prof-notif-sub">
                {user.emailNotificationsEnabled
                  ? 'You will receive an email after each analysis.'
                  : 'Notifications are disabled.'}
              </p>
            </div>
            <button
              className={`prof-toggle ${user.emailNotificationsEnabled ? 'is-on' : ''}`}
              onClick={handleNotifToggle}
              aria-label="Toggle notifications"
            >
              <span className="prof-toggle-knob" />
            </button>
          </div>
          <button
            type="button"
            className="prof-btn"
            onClick={handleTestEmail}
            disabled={sendingTestEmail}
          >
            {sendingTestEmail ? 'Sending…' : 'Send test email'}
          </button>
        </div>

        {/* ── Change password ── */}
        <div className="prof-card">
          <h2 className="prof-card-title">Change password</h2>
          <form onSubmit={handleChangePw} className="prof-form">
            <label className="prof-label">
              Current password
              <input
                className="prof-input"
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password"
              />
            </label>
            <label className="prof-label">
              New password
              <input
                className="prof-input"
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label className="prof-label">
              Confirm new password
              <input
                className="prof-input"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            {pwMsg && (
              <p className={`prof-msg ${pwMsg.type === 'ok' ? 'prof-msg--ok' : 'prof-msg--err'}`}>
                {pwMsg.text}
              </p>
            )}
            <button className="prof-btn prof-btn--primary" type="submit" disabled={savingPw}>
              {savingPw ? 'Saving…' : 'Change password'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
